import React, { useEffect, useState } from 'react';
import { priceTooltipText } from '../../utils/number';
import PriceCostBasisHint from '../../components/PriceCostBasisHint';

interface Listing {
 listing_hash: string;
 mo_family_hash: string;
 side: 'seeking_advertiser' | 'seeking_media';
 period_start: string;
 period_end: string;
 price_per_impression: string;
 daily_budget_cap?: string | number | null;
 guaranteed_impressions?: number | null;
}

interface Owner { mo_peer_hash: string; name: string; }

interface Video {
 file_hash: string;
 filename: string;
 mime_type?: string;
 duration?: number;
 thumbnail_path?: string;
}

interface MyFamily {
 moFamilyHash: string;
 name: string;
}

interface Props {
 listing: Listing;
 owner: Owner;
 videos: Video[];
 onClose: () => void;
 onCreated: (proposalHash: string) => void;
}

// ─── 공통 폼 조각 — Type 1/2 모두 사용 ────────────────────────────────
const SelectField: React.FC<{
 label: string;
 value: string;
 onChange: (v: string) => void;
 options: { value: string; label: string }[];
 disabled?: boolean;
 hint?: string;
 emptyHint?: string | null;
}> = ({ label, value, onChange, options, disabled, hint, emptyHint }) => (
 <div>
  <label className="block text-gray-600 mb-1">{label}</label>
  <select
   value={value}
   onChange={(e) => onChange(e.target.value)}
   disabled={disabled}
   className="w-full px-2 py-1 border border-gray-300 disabled:opacity-50"
  >
   <option value="">선택...</option>
   {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
  {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  {emptyHint && <p className="text-xs text-gray-500 mt-1">{emptyHint}</p>}
 </div>
);

const CommonProposalFields: React.FC<{
 periodStart: string; periodEnd: string;
 price: string; dailyBudgetCap: string; guaranteedImpressions: string;
 message: string;
 error: string | null; submitting: boolean;
 onPeriodStart: (v: string) => void; onPeriodEnd: (v: string) => void;
 onPrice: (v: string) => void;
 onDailyBudgetCap: (v: string) => void; onGuaranteedImpressions: (v: string) => void;
 onMessage: (v: string) => void;
 onSubmit: () => void; onClose: () => void;
}> = (p) => (
 <>
  <div className="grid grid-cols-2 gap-2">
   <div>
    <label className="block text-gray-600 mb-1">기간 시작 *</label>
    <input type="date" value={p.periodStart} onChange={e => p.onPeriodStart(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
   </div>
   <div>
    <label className="block text-gray-600 mb-1">기간 종료 *</label>
    <input type="date" value={p.periodEnd} onChange={e => p.onPeriodEnd(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
   </div>
  </div>
  <div>
   <label className="block text-gray-600 mb-1">
    단가 (원/회){' '}
    <span
     className="text-gray-400 cursor-help"
     title={priceTooltipText(Number(p.price))}
    >ⓘ</span>
   </label>
   <input
    type="number" step="0.1" min="0"
    value={p.price} onChange={e => p.onPrice(e.target.value)}
    title={priceTooltipText(Number(p.price))}
    className="w-32 px-2 py-1 border border-gray-300"
   />
  </div>
  <div className="grid grid-cols-2 gap-2">
   <div>
    <label className="block text-gray-600 mb-1">
     하루 최대 책정 금액 (원){' '}
     <span
      className="text-gray-400 cursor-help"
      title={'광고주 안전 장치 — 그 날 송출이 캡을 넘어도 청구는 캡까지만.\n송출은 계속됩니다 (잉여분은 미디어사 무상 노출).'}
     >ⓘ</span>
    </label>
    <input
     type="number" step="100" min="0"
     value={p.dailyBudgetCap} onChange={e => p.onDailyBudgetCap(e.target.value)}
     placeholder="(미지정 = 한도 없음)"
     className="w-full px-2 py-1 border border-gray-300"
    />
    <p className="text-[10px] text-gray-500 mt-0.5">캡까지만 청구 (송출은 계속)</p>
   </div>
   <div>
    <label className="block text-gray-600 mb-1">
     송출 횟수 보장 (전 기간){' '}
     <span
      className="text-gray-400 cursor-help"
      title={'기간 종료 시점 보장치 대비:\n· 50% 미만 송출 → 전액 환불\n· 50~100% 구간 → 실제 송출 비율로 비례 청구\n· 100% 이상 → 보장치 × 단가 정액 청구'}
     >ⓘ</span>
    </label>
    <input
     type="number" step="100" min="0"
     value={p.guaranteedImpressions} onChange={e => p.onGuaranteedImpressions(e.target.value)}
     placeholder="(미지정 = 보장 없음)"
     className="w-full px-2 py-1 border border-gray-300"
    />
    <p className="text-[10px] text-gray-500 mt-0.5">50% 미만 = 전액 환불 / 50~100% = 비례 청구</p>
   </div>
  </div>
  <div>
   <label className="block text-gray-600 mb-1">메시지 (선택)</label>
   <textarea value={p.message} onChange={e => p.onMessage(e.target.value)} rows={3} className="w-full px-2 py-1 border border-gray-300" />
  </div>
  {p.error && <div className="bg-red-50 text-red-700 p-2 text-xs">{p.error}</div>}
  <div className="text-xs text-gray-500">제안은 보낸 후 2일 내에 응답이 없으면 자동 만료됩니다.</div>
  <div className="flex gap-2 pt-2">
   <button onClick={p.onSubmit} disabled={p.submitting}
    className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50">
    {p.submitting ? '보내는 중...' : '제안 보내기'}
   </button>
   <button onClick={p.onClose} className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300">취소</button>
  </div>
 </>
);

// Type 2 광고주 모패밀리 선택 모드
//  - 'existing': 이미 영상을 슬롯에 넣어 둔 모패밀리 선택 (기존 동작)
//  - 'auto':     영상 하나 골라 1슬롯 모패밀리를 자동 생성
type AdvertiserMode = 'existing' | 'auto';

const ProposalCreate: React.FC<Props> = ({ listing, owner, videos, onClose, onCreated }) => {
 const [myFamilies, setMyFamilies] = useState<MyFamily[]>([]);
 const [mediaFamilyHash, setMediaFamilyHash] = useState<string>('');     // Type 1: 본인 미디어 패밀리
 const [advertiserFamilyHash, setAdvertiserFamilyHash] = useState<string>(''); // Type 2 existing: 본인 광고주 패밀리
 const [myVideos, setMyVideos] = useState<Video[]>([]);                  // Type 2 existing: 슬롯 점유 영상
 const [allMyVideos, setAllMyVideos] = useState<Video[]>([]);            // Type 2 auto: 내 모든 업로드 영상
 const [advertiserMode, setAdvertiserMode] = useState<AdvertiserMode>('auto');
 const [fileHash, setFileHash] = useState<string>('');
 const [periodStart, setPeriodStart] = useState(listing.period_start);
 const [periodEnd, setPeriodEnd] = useState(listing.period_end);
 const [price, setPrice] = useState(String(listing.price_per_impression));
 const [dailyBudgetCap, setDailyBudgetCap] = useState(
  listing.daily_budget_cap != null ? String(listing.daily_budget_cap) : ''
 );
 const [guaranteedImpressions, setGuaranteedImpressions] = useState(
  listing.guaranteed_impressions != null ? String(listing.guaranteed_impressions) : ''
 );
 const [message, setMessage] = useState('');
 const [error, setError] = useState<string | null>(null);
 const [submitting, setSubmitting] = useState(false);

 // 광고주 listing(seeking_media) → Type 1 (미디어사가 광고 받겠다고 보냄)
 // 미디어사 listing(seeking_advertiser) → Type 2 (광고주가 광고 보내겠다고 보냄)
 const proposalType: 1 | 2 = listing.side === 'seeking_media' ? 1 : 2;

 useEffect(() => {
  fetch('/mofamily/my-families', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    const fams: MyFamily[] = (j.data || []).filter((f: any) => f.role === 'owner');
    setMyFamilies(fams);
    if (fams.length === 1) {
     if (proposalType === 1) setMediaFamilyHash(fams[0].moFamilyHash);
     else setAdvertiserFamilyHash(fams[0].moFamilyHash);
    }
   });
 }, [proposalType]);

 // Type 2 auto 모드용 — 내 업로드 영상 전체 list 한 번 fetch
 useEffect(() => {
  if (proposalType !== 2) return;
  fetch('/mofile/', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    const list: Video[] = (j.data || [])
     .filter((f: any) => (f.mime_type || f.file_type)?.startsWith('video/'))
     .map((f: any) => ({
      file_hash: f.file_hash,
      filename: f.filename || f.title,
      mime_type: f.mime_type || f.file_type,
      duration: f.duration,
      thumbnail_path: f.thumbnail_path,
     }));
    setAllMyVideos(list);
   })
   .catch(() => setAllMyVideos([]));
 }, [proposalType]);

 // Type 2 existing: 광고주 패밀리가 바뀌면 그 패밀리의 슬롯 점유 영상 fetch.
 useEffect(() => {
  if (proposalType !== 2 || advertiserMode !== 'existing' || !advertiserFamilyHash) {
   setMyVideos([]);
   return;
  }
  fetch(`/market/listings/family/${advertiserFamilyHash}/slot-videos`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    const list = (j.data || []).filter((f: any) => f.mime_type?.startsWith('video/'));
    setMyVideos(list);
   })
   .catch(() => setMyVideos([]));
  setFileHash('');
 }, [proposalType, advertiserMode, advertiserFamilyHash]);

 // 모드 전환 시 선택값 초기화 — 영상/패밀리 매핑이 모드별로 다름
 useEffect(() => {
  setFileHash('');
 }, [advertiserMode]);

 const submit = async () => {
  if (!fileHash) { setError('광고 영상을 선택하세요.'); return; }
  if (proposalType === 1 && !mediaFamilyHash) {
   setError('내 미디어 모패밀리를 선택하세요.');
   return;
  }
  if (proposalType === 2 && advertiserMode === 'existing' && !advertiserFamilyHash) {
   setError('내 광고주 모패밀리를 선택하세요.');
   return;
  }
  setSubmitting(true);
  setError(null);
  try {
   // Type 2 auto 모드 — 먼저 1슬롯 모패밀리 자동 생성
   let resolvedAdvertiserHash = advertiserFamilyHash;
   if (proposalType === 2 && advertiserMode === 'auto') {
    const r = await fetch('/market/proposals/auto-advertiser-family', {
     method: 'POST',
     headers: { 'Content-Type': 'application/json' },
     credentials: 'include',
     body: JSON.stringify({ file_hash: fileHash }),
    });
    const jj = await r.json();
    if (!r.ok || !jj.success) throw new Error(jj.error || '광고주 모패밀리 자동 생성 실패');
    resolvedAdvertiserHash = jj.data.mo_family_hash;
   }

   const payloadCommon = {
    file_hash: fileHash,
    period_start: periodStart,
    period_end: periodEnd,
    price_per_impression: Number(price),
    daily_budget_cap: dailyBudgetCap ? Number(dailyBudgetCap) : null,
    guaranteed_impressions: guaranteedImpressions ? Number(guaranteedImpressions) : null,
    message: message || undefined,
   };
   const body = {
    type: proposalType,
    listing_hash: listing.listing_hash,
    recipient_mopeer: owner.mo_peer_hash,
    payload: proposalType === 1
     ? { ...payloadCommon, media_family_hash: mediaFamilyHash }
     : { ...payloadCommon, advertiser_family_hash: resolvedAdvertiserHash },
   };
   const res = await fetch('/market/proposals', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
   });
   const j = await res.json();
   if (!res.ok || !j.success) throw new Error(j.error || '제안 생성 실패');
   onCreated(j.data.proposal_hash);
  } catch (err: any) {
   setError(err.message || '제안 생성 실패');
  } finally {
   setSubmitting(false);
  }
 };

 return (
  <div
   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
   onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
   <div className="bg-white p-3 md:p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
    <div className="flex justify-between items-center mb-3">
     <h2 className="text-lg md:text-xl font-bold">
      {proposalType === 1 ? '광고를 받겠습니다 (Type 1)' : '광고를 보내겠습니다 (Type 2)'}
     </h2>
     <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
    </div>

    <div className="space-y-3 text-sm">
     {/* type 별로 영상 picker / 모패밀리 picker 만 다름. 기간/단가/메시지/에러/버튼은 공통. */}
     {proposalType === 1 ? (
      <>
       <SelectField
        label="광고 영상 *"
        value={fileHash}
        onChange={setFileHash}
        options={videos.map(v => ({ value: v.file_hash, label: v.filename }))}
        emptyHint={videos.length === 0 ? '상대 모패밀리에 노출된 광고 영상이 없습니다.' : null}
       />
       <SelectField
        label="내 미디어 모패밀리 *"
        value={mediaFamilyHash}
        onChange={setMediaFamilyHash}
        options={myFamilies.map(f => ({ value: f.moFamilyHash, label: f.name }))}
        hint="광고가 송출될 모패밀리. 활성 슬롯 세트의 첫 빈 슬롯에 점유됩니다."
       />
      </>
     ) : (
      <>
       <div>
        <label className="block text-gray-600 mb-1">내 광고주 모패밀리 *</label>
        <div className="flex gap-3 text-xs mb-2">
         <label className="flex items-center gap-1 cursor-pointer">
          <input
           type="radio"
           name="advertiserMode"
           checked={advertiserMode === 'auto'}
           onChange={() => setAdvertiserMode('auto')}
          />
          <span>영상 선택하면 자동 모패밀리 생성</span>
         </label>
         <label className="flex items-center gap-1 cursor-pointer">
          <input
           type="radio"
           name="advertiserMode"
           checked={advertiserMode === 'existing'}
           onChange={() => setAdvertiserMode('existing')}
          />
          <span>광고 영상을 넣어 둔 모패밀리 선택</span>
         </label>
        </div>

        {advertiserMode === 'auto' ? (
         <>
          <SelectField
           label="광고 영상 *"
           value={fileHash}
           onChange={setFileHash}
           options={allMyVideos.map(v => ({ value: v.file_hash, label: v.filename }))}
           emptyHint={allMyVideos.length === 0 ? '업로드한 광고 영상이 없습니다. 영상 메뉴에서 먼저 업로드하세요.' : null}
           hint="선택한 영상으로 1슬롯 광고주 모패밀리가 자동 생성됩니다 (이름 = 영상 파일명)."
          />
         </>
        ) : (
         <>
          <SelectField
           label="모패밀리 *"
           value={advertiserFamilyHash}
           onChange={setAdvertiserFamilyHash}
           options={myFamilies.map(f => ({ value: f.moFamilyHash, label: f.name }))}
           hint="광고 영상이 슬롯에 등록되어 있는 본인 모패밀리."
          />
          <SelectField
           label="광고 영상 *"
           value={fileHash}
           onChange={setFileHash}
           options={myVideos.map(v => ({ value: v.file_hash, label: v.filename }))}
           disabled={!advertiserFamilyHash}
           emptyHint={advertiserFamilyHash && myVideos.length === 0 ? '이 모패밀리의 슬롯에 등록된 광고 영상이 없습니다. 모패밀리 상세 → 슬롯 관리에서 영상을 먼저 할당하세요.' : null}
          />
         </>
        )}
       </div>
      </>
     )}
     {/* Type 1 (내가 미디어): mediaFamilyHash 의 cost-basis 기준 권장 단가 안내.
         Type 2 existing: advertiserFamilyHash 기준. Type 2 auto: 새 패밀리라 비용 없음 → 노출 안 됨. */}
     {(proposalType === 1 && mediaFamilyHash) && (
      <PriceCostBasisHint moFamilyHash={mediaFamilyHash} pricePerImpression={Number(price)} />
     )}
     {(proposalType === 2 && advertiserMode === 'existing' && advertiserFamilyHash) && (
      <PriceCostBasisHint moFamilyHash={advertiserFamilyHash} pricePerImpression={Number(price)} />
     )}
     <CommonProposalFields
      periodStart={periodStart} periodEnd={periodEnd}
      price={price}
      dailyBudgetCap={dailyBudgetCap}
      guaranteedImpressions={guaranteedImpressions}
      message={message} error={error} submitting={submitting}
      onPeriodStart={setPeriodStart} onPeriodEnd={setPeriodEnd}
      onPrice={setPrice}
      onDailyBudgetCap={setDailyBudgetCap}
      onGuaranteedImpressions={setGuaranteedImpressions}
      onMessage={setMessage}
      onSubmit={submit} onClose={onClose}
     />
    </div>
   </div>
  </div>
 );
};

export default ProposalCreate;
